// Regras de texto e de tempo do topo ao vivo — sem React, para dar pra testar.
//
// O backend manda o ESTADO da instância (pulso.estado); aqui ele vira o que o
// cliente lê: título, subtítulo, cor do farol e o selo do canto.

export const CORES = {
  ok: '#059669',
  atencao: '#f59e0b',
  erro: '#eb3c3c',
  neutro: '#8b95a1',
}

// Um estado -> como o topo se apresenta. `animado` desliga o pulso nos estados
// em que nada está acontecendo (pulsar "parado" confunde).
export const ESTADOS = {
  enviando: { selo: 'AO VIVO', cor: CORES.ok, titulo: 'Conectada', animado: true },
  ociosa: { selo: 'AO VIVO', cor: CORES.ok, titulo: 'Conectada', animado: true },
  foraDoExpediente: { selo: 'FORA DO EXPEDIENTE', cor: CORES.atencao, titulo: 'Conectada', animado: false },
  limiteDiario: { selo: 'LIMITE DIÁRIO', cor: CORES.atencao, titulo: 'Conectada', animado: false },
  parada: { selo: 'FILA PARADA', cor: CORES.atencao, titulo: 'Conectada', animado: false },
  desconectada: { selo: 'SEM CONEXÃO', cor: CORES.erro, titulo: 'Desconectada', animado: false },
  suspensa: { selo: 'SUSPENSA', cor: CORES.erro, titulo: 'Instância suspensa', animado: false },
}

const hhmm = (iso) =>
  new Date(iso).toLocaleTimeString('pt-BR', { timeZone: 'America/Sao_Paulo', hour: '2-digit', minute: '2-digit' })

const DIAS = ['domingo', 'segunda', 'terça', 'quarta', 'quinta', 'sexta', 'sábado']

// "hoje às 08:00", "amanhã às 08:00" ou "segunda às 08:00" — no fuso de São Paulo.
export function quandoRetoma(iso, agora = new Date()) {
  if (!iso) return null
  const diaSP = (d) => new Date(new Date(d).toLocaleString('en-US', { timeZone: 'America/Sao_Paulo' }))
  const alvo = diaSP(iso)
  const hoje = diaSP(agora)
  const zera = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime()
  const diff = Math.round((zera(alvo) - zera(hoje)) / 864e5)
  const hora = hhmm(iso)
  if (diff <= 0) return `hoje às ${hora}`
  if (diff === 1) return `amanhã às ${hora}`
  return `${DIAS[alvo.getDay()]} às ${hora}`
}

export function subtitulo(pulso, { metaOficial } = {}) {
  if (!pulso) return 'Buscando o estado da instância…'
  const { estado, expediente, fila } = pulso
  switch (estado) {
    case 'enviando':
      return fila && fila.andando > 0 ? 'Enviando a fila agora' : 'Terminou de enviar há pouco'
    case 'ociosa':
      return metaOficial ? 'Conta Meta pronta · nada na fila' : 'WhatsApp pronto · nada na fila'
    case 'foraDoExpediente': {
      const retoma = quandoRetoma(expediente && expediente.retomaEm)
      return retoma ? `Envios retomam ${retoma}` : 'Aguardando o expediente para enviar'
    }
    case 'limiteDiario':
      return 'Teto diário de envios atingido · libera na virada do dia'
    case 'parada':
      return 'Tem mensagem na fila, mas nada saiu nos últimos minutos'
    case 'desconectada':
      return 'Envios suspensos até reconectar o WhatsApp'
    case 'suspensa':
      return 'Fale com o suporte para reativar os envios'
    default:
      return ''
  }
}

export function textoExpediente(expediente) {
  if (!expediente || !expediente.ativo) return 'sem restrição de horário'
  if (expediente.aberto) return expediente.fechaEm ? `aberto até ${hhmm(expediente.fechaEm)}` : 'aberto'
  const retoma = quandoRetoma(expediente.retomaEm)
  return retoma ? `fechado · abre ${retoma}` : 'fechado'
}

// "agora", "há 12s", "há 3 min", "há 2 h"
export function haQuanto(iso, agoraMs = Date.now()) {
  if (!iso) return '—'
  const s = Math.max(0, Math.round((agoraMs - new Date(iso).getTime()) / 1000))
  if (s < 3) return 'agora'
  if (s < 60) return `há ${s}s`
  const m = Math.round(s / 60)
  if (m < 60) return `há ${m} min`
  const h = Math.round(m / 60)
  if (h < 48) return `há ${h} h`
  return `há ${Math.round(h / 24)} dias`
}

// Quanto falta para a fila esvaziar no ritmo atual.
export function previsaoTermino(andando, porMinuto) {
  if (!andando) return 'fila vazia'
  if (!porMinuto) return '—'
  const min = andando / porMinuto
  if (min < 1) return 'menos de 1 min'
  if (min < 60) return `~${Math.ceil(min)} min`
  const h = Math.floor(min / 60)
  const resto = Math.round(min % 60)
  return resto ? `~${h} h ${resto} min` : `~${h} h`
}

// Horário do feed "Agora mesmo". Evento de hoje mostra só a hora (com
// segundos, que é o que dá a sensação de ao vivo); de outro dia leva "ontem"
// ou a data — senão uma saída de ontem às 18:55 parece ter sido agora há pouco.
export function horaDoEvento(iso, agora = new Date()) {
  const tz = { timeZone: 'America/Sao_Paulo' }
  const dia = (d) => new Date(d).toLocaleDateString('pt-BR', tz)
  const hora = (opcoes) => new Date(iso).toLocaleTimeString('pt-BR', { ...tz, hour: '2-digit', minute: '2-digit', ...opcoes })
  if (dia(iso) === dia(agora)) return hora({ second: '2-digit' })
  if (dia(iso) === dia(new Date(new Date(agora).getTime() - 864e5))) return `ontem ${hora()}`
  return `${dia(iso).slice(0, 5)} ${hora()}`
}

// Idade da mensagem mais antiga que ainda anda na fila (fila.maisAntigaEm do
// pulso: o created_at dela, em ISO, ou null com a fila vazia). Hoje vira "há
// 3 h"; de outro dia vira "desde ontem, 18:20" ou "desde 01/11, 18:20" — tudo
// no fuso de São Paulo. Passando de 12 h a fila pede atenção: com 24 h a
// mensagem é descartada por idade.
export const HORAS_ATENCAO_FILA = 12

export function idadeDaFila(iso, agora = new Date()) {
  if (!iso) return null
  const inicio = new Date(iso)
  if (Number.isNaN(inicio.getTime())) return null
  const agoraMs = new Date(agora).getTime()
  const horas = (agoraMs - inicio.getTime()) / 36e5
  const tz = { timeZone: 'America/Sao_Paulo' }
  const dia = (d) => new Date(d).toLocaleDateString('pt-BR', tz)
  const hora = hhmm(inicio)
  let texto
  if (dia(inicio) === dia(agoraMs)) texto = haQuanto(inicio.toISOString(), agoraMs)
  else if (dia(inicio) === dia(agoraMs - 864e5)) texto = `desde ontem, ${hora}`
  else texto = `desde ${dia(inicio).slice(0, 5)}, ${hora}`
  return { texto, horas, atencao: horas >= HORAS_ATENCAO_FILA }
}

// Número parcialmente escondido no feed ao vivo — a tela costuma ser projetada
// ou compartilhada. O número completo continua na tabela logo abaixo.
export function mascararTelefone(raw) {
  const d = String(raw || '').replace(/\D/g, '')
  if (d.length < 8) return raw || '—'
  const local = d.length >= 12 && d.startsWith('55') ? d.slice(2) : d
  const ddd = local.length >= 10 ? local.slice(0, 2) : ''
  return `${ddd ? `(${ddd}) ` : ''}••••-${local.slice(-4)}`
}

const estadoLib = { ESTADOS, CORES, horaDoEvento, idadeDaFila, subtitulo, textoExpediente, haQuanto, previsaoTermino, mascararTelefone, quandoRetoma }

export default estadoLib
